import { visit } from "@hotwired/turbo";
import { useState } from "react";
import { formatInTimeZone } from "date-fns-tz";
import { Copy, Check } from "lucide-react";
import Breadcrumbs from "../shared/Breadcrumbs";

// #STYLE_GUIDE: TypeScript interfaces use camelCase (props are transformed by turbo-mount)
interface ExpiringLinkProps {
  id: string;
  slug: string;
  avatarId: string;
  timezone: string;
  expiresAt: string;
  fullUrl: string;
  expired: boolean;
  createdAt: string;
  updatedAt: string;
  avatar?: {
    id: string;
    displayName: string;
  };
  brains?: Array<{
    id: string;
    displayName: string;
  }>;
}

interface ExpiringLinkShowProps {
  expiringLink: ExpiringLinkProps;
}

// #STYLE_GUIDE: Show component displays single record details
// - Uses daisyUI components for consistent styling
// - Navigation via Turbo.visit() for SPA-like experience
export default function ExpiringLinkShow({
  expiringLink,
}: ExpiringLinkShowProps) {
  const [copied, setCopied] = useState(false);

  const handleEdit = () => {
    // #TURBO: Use Turbo.visit for navigation
    visit(`/expiring_links/${expiringLink.id}/edit`);
  };

  const handleBack = () => {
    // #TURBO: Use Turbo.visit for navigation
    visit("/dashboard/index");
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(expiringLink.fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const formattedExpiration = formatInTimeZone(
    new Date(expiringLink.expiresAt),
    expiringLink.timezone,
    "MMM dd, yyyy 'at' h:mm a zzz"
  );

  return (
    <div className="space-y-6">
      {/* Breadcrumbs */}
      <Breadcrumbs items={[{ label: `Link: ${expiringLink.slug}` }]} />

      {/* Header with actions */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Expiring Link</h1>
        <div className="join">
          <button onClick={handleBack} className="btn btn-outline join-item">
            Back to Dashboard
          </button>
          <button onClick={handleEdit} className="btn btn-primary join-item">
            Edit
          </button>
        </div>
      </div>

      {/* Prominent Copy Link Section */}
      <div className="card bg-primary text-primary-content shadow-xl">
        <div className="card-body">
          <h2 className="card-title text-2xl mb-4">🔗 Share This Link</h2>
          <div className="bg-base-100 text-base-content rounded-lg p-4 mb-4">
            <code className="text-lg break-all">{expiringLink.fullUrl}</code>
          </div>
          <button
            onClick={copyToClipboard}
            className="btn btn-lg btn-accent w-full gap-2"
          >
            {copied ? (
              <>
                <Check className="w-6 h-6" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="w-6 h-6" />
                Copy Link
              </>
            )}
          </button>
          <div className="mt-4 text-center">
            <p className="text-sm opacity-90">Expires: {formattedExpiration}</p>
            {expiringLink.expired && (
              <div className="badge badge-error mt-2">Expired</div>
            )}
          </div>
        </div>
      </div>

      {/* Details card */}
      <div className="card bg-base-100 shadow-xl">
        <div className="card-body">
          <h3 className="card-title">Link Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">
                <span className="label-text font-semibold">Slug</span>
              </label>
              <p className="text-base-content">{expiringLink.slug}</p>
            </div>

            <div>
              <label className="label">
                <span className="label-text font-semibold">Avatar</span>
              </label>
              <p className="text-base-content">
                {expiringLink.avatar?.displayName || "Not set"}
              </p>
            </div>

            <div>
              <label className="label">
                <span className="label-text font-semibold">Timezone</span>
              </label>
              <p className="text-base-content">{expiringLink.timezone}</p>
            </div>

            <div>
              <label className="label">
                <span className="label-text font-semibold">Status</span>
              </label>
              <p className="text-base-content">
                {expiringLink.expired ? (
                  <span className="badge badge-error">Expired</span>
                ) : (
                  <span className="badge badge-success">Active</span>
                )}
              </p>
            </div>

            <div className="md:col-span-2">
              <label className="label">
                <span className="label-text font-semibold">
                  Selected Brains ({expiringLink.brains?.length || 0})
                </span>
              </label>
              {expiringLink.brains && expiringLink.brains.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {expiringLink.brains.map((brain) => (
                    <span key={brain.id} className="badge badge-outline">
                      {brain.displayName}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-base-content/60">No brains selected</p>
              )}
            </div>

            <div>
              <label className="label">
                <span className="label-text font-semibold">Created</span>
              </label>
              <p className="text-base-content">
                {new Date(expiringLink.createdAt).toLocaleString()}
              </p>
            </div>

            <div>
              <label className="label">
                <span className="label-text font-semibold">Last Updated</span>
              </label>
              <p className="text-base-content">
                {new Date(expiringLink.updatedAt).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
